import { find, upperFirst } from 'lodash-es';

import { Pages } from '@/types/Routing';

import { AutocompleteQueries } from '../landing/landing-search.types';
import { DEFAULT_LANGUAGE, LISTING_FILTERS_STORAGE_KEY, ListingQueryParams } from './constants';
import { ListingData, ListingRequest } from './listing.types';

const ADDITIONAL_OFFSET = 12;

export const scrollDoctorListingInView = (): void => {
  if (typeof window !== 'undefined') {
    const doctorsListingPage = document.getElementById('doctors-listing');
    const header =
      document.getElementById('neu-app')?.closest('neu-app-header') ||
      document.querySelector('.header-sticky')?.closest('header');
    const pageTitleBanner = document.getElementById('oas-page-title-banner');

    const pageTitleBannerHeight = pageTitleBanner
      ? pageTitleBanner.getBoundingClientRect().height
      : 0;
    const headerHeight = header ? header.getBoundingClientRect().height : 0;

    if (doctorsListingPage) {
      const elementPosition = doctorsListingPage.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - headerHeight - pageTitleBannerHeight + ADDITIONAL_OFFSET,
        behavior: 'smooth',
      });
    }
  }
};

export const deriveQueryParams = (filtersQueryParams: Partial<ListingRequest>): URLSearchParams => {
  const queryParams = new URLSearchParams();

  if (filtersQueryParams.zip) {
    queryParams.set(ListingQueryParams.zip, filtersQueryParams.zip);
  }

  if (filtersQueryParams.keywords) {
    queryParams.set(ListingQueryParams.keywords, filtersQueryParams.keywords);
  }

  if (filtersQueryParams.keywordExact) {
    queryParams.set(ListingQueryParams.keywordExact, filtersQueryParams.keywordExact);
  }

  if (
    filtersQueryParams.specialtyName ||
    filtersQueryParams.specialty ||
    (filtersQueryParams.locationUrlTitle && filtersQueryParams.keywords)
  ) {
    queryParams.delete(ListingQueryParams.keywords);
  }

  if (filtersQueryParams.gender) {
    queryParams.set(ListingQueryParams.gender, filtersQueryParams.gender);
  }

  if (
    filtersQueryParams.languages &&
    filtersQueryParams.languages?.length > 0 &&
    filtersQueryParams.languages.split(';').some((lang) => lang !== DEFAULT_LANGUAGE)
  ) {
    queryParams.set(ListingQueryParams.languages, filtersQueryParams.languages);
  }

  if (filtersQueryParams.showAcceptingNewPatientsOnly) {
    queryParams.set(
      ListingQueryParams.acceptingNewPatientsOnly,
      filtersQueryParams.showAcceptingNewPatientsOnly.toString()
    );
  }

  if (filtersQueryParams.teleMedicineFlg) {
    queryParams.set(
      ListingQueryParams.teleMedicineFlg,
      filtersQueryParams.teleMedicineFlg.toString()
    );
  }

  if (filtersQueryParams.facilityCoid && filtersQueryParams.locationUrlTitle) {
    queryParams.set(ListingQueryParams.locationUrlTitle, filtersQueryParams.locationUrlTitle);
    queryParams.set(ListingQueryParams.facilityCoid, filtersQueryParams.facilityCoid);
  }

  if (filtersQueryParams.specialtyName || filtersQueryParams.specialty) {
    queryParams.set(
      ListingQueryParams.specialty,
      filtersQueryParams.specialty || filtersQueryParams.specialtyName || ''
    );
  }

  if (filtersQueryParams.locationUrlTitle && filtersQueryParams.locationUrlTitleLabel) {
    queryParams.set(ListingQueryParams.locationUrlTitle, filtersQueryParams.locationUrlTitle);
    queryParams.set(
      ListingQueryParams.locationUrlTitleLabel,
      filtersQueryParams.locationUrlTitleLabel
    );
  }

  return queryParams;
};

/**
 * Builds a listing page URL with optional filter query parameters
 * Used to preserve filter state when navigating back to listing via breadcrumbs
 *
 * @param baseHref - The base listing page URL
 * @param filters - Optional applied filters to append as query parameters
 * @returns Complete URL with query parameters if filters are provided
 */
export const buildListingUrlWithFilters = (
  baseHref: string,
  filters?: Partial<ListingRequest>
): string => {
  if (!filters) {
    return baseHref;
  }

  const queryParams = deriveQueryParams(filters);
  const queryString = queryParams.toString();

  return queryString ? `${baseHref}?${queryString}` : baseHref;
};

/**
 * Retrieves stored listing filters from sessionStorage
 * Used by the provider to get filters when not on listing page
 */
export const getStoredListingFilters = (): Partial<ListingRequest> | undefined => {
  if (typeof window !== 'undefined') {
    try {
      const stored = sessionStorage.getItem(LISTING_FILTERS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : undefined;
    } catch (error) {
      console.error('Failed to retrieve listing filters:', error);
      return undefined;
    }
  }
  return undefined;
};

/**
 * Checks if query params contain any filter values
 * Uses ListingQueryParams enum to dynamically check all filter fields
 */
function hasActiveFilters(queryParams: ReturnType<typeof getQueryParams>): boolean {
  if (!queryParams) {
    return false;
  }

  // Check each filter field for truthy values
  return !!(
    queryParams.zip ||
    queryParams.selectedGenders?.length ||
    queryParams.selectedLanguages?.length ||
    queryParams.showAcceptingNewPatientsOnly ||
    queryParams.teleMedicineFlg ||
    queryParams.facilityCoid ||
    queryParams.keywordExact ||
    queryParams.keywords ||
    queryParams.specialtyId ||
    queryParams.specialtyName ||
    queryParams.specialty ||
    queryParams.locationUrlTitle
  );
}

/**
 * Converts query params to ListingRequest filter object
 */
function queryParamsToFilters(
  queryParams: ReturnType<typeof getQueryParams>
): Partial<ListingRequest> {
  if (!queryParams) {
    return {};
  }

  return {
    zip: queryParams.zip || undefined,
    gender: queryParams.selectedGenders?.join(',') || undefined,
    languages: queryParams.selectedLanguages?.join(';') || undefined,
    showAcceptingNewPatientsOnly: queryParams.showAcceptingNewPatientsOnly,
    teleMedicineFlg: queryParams.teleMedicineFlg,
    facilityCoid: queryParams.facilityCoid || undefined,
    keywordExact: queryParams.keywordExact || undefined,
    keywords: queryParams.keywords || undefined,
    specialtyId: queryParams.specialtyId || undefined,
    specialtyName: queryParams.specialtyName || queryParams.specialty || undefined,
    specialty: queryParams.specialty || undefined,
    locationUrlTitle: queryParams.locationUrlTitle || undefined,
    locationUrlTitleLabel: queryParams.locationUrlTitleLabel || undefined,
  };
}

/**
 * Extracts applied filters from URL query params or sessionStorage.
 * First checks URL params, then falls back to sessionStorage if user navigated away from listing.
 * This is the single source of truth for filter extraction used across the application.
 *
 * @returns Applied filters object or undefined if no filters present
 */
export const getAppliedFiltersForBreadcrumbs = (): Partial<ListingRequest> | undefined => {
  const queryParams = getQueryParams();

  // Only create appliedFilters if there are actual filter values present
  if (hasActiveFilters(queryParams)) {
    return queryParamsToFilters(queryParams);
  }

  // If no filters from URL, check sessionStorage (user navigated away from listing)
  return getStoredListingFilters();
};

type Specialties = Array<{
  id: string;
  name: string;
}>;

/**
 * Normalizes a string for specialty comparison by:
 * - Converting to lowercase
 * - Removing special characters (commas, ampersands, slashes, apostrophes, parentheses, etc.)
 * - Replacing dashes and multiple spaces with single spaces
 * - Trimming whitespace
 *
 * Examples:
 * - "Allergy and Immunology" -> "allergy and immunology"
 * - "allergy-and-immunology" -> "allergy and immunology"
 * - "Endocrinology - Diabetes and Metabolism" -> "endocrinology diabetes and metabolism"
 * - "endocrinology-diabetes-and-metabolism" -> "endocrinology diabetes and metabolism"
 * - "Otolaryngology (ENT)" -> "otolaryngology ent"
 * - "otolaryngology-ent" -> "otolaryngology ent"
 * - "Pediatric Hematology - Medical Oncology" -> "pediatric hematology medical oncology"
 * - "pediatric-hematology-medical-oncology" -> "pediatric hematology medical oncology"
 */
const normalizeForComparison = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[,&/()]/g, ' ') // Replace commas, ampersands, slashes, parentheses with spaces
    .replace(/[']/g, '') // Remove apostrophes
    .replace(/[-]+/g, ' ') // Replace dashes with spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

/**
 * Checks if a given slug matches a specialty from the specialties list
 * @param slug - The URL slug to check (e.g., "ear-nose-throat", "ob-gyn", "womens-health")
 * @param specialties - Array of specialty objects with id and name
 * @returns true if the slug matches a specialty, false otherwise
 *
 * @example
 * // Specialty name: "Ear, Nose & Throat"
 * isSpecialtySlug("ear-nose-throat", specialties) // returns true
 *
 * @example
 * // Specialty name: "OB/GYN"
 * isSpecialtySlug("ob-gyn", specialties) // returns true
 */
export const isSpecialtySlug = (slug: string, specialties: Specialties): boolean => {
  if (!slug || !specialties || specialties.length === 0) {
    return false;
  }

  const normalizedSlug = normalizeForComparison(slug);

  // If slug normalizes to empty string (e.g., just special chars like "---"), return false
  if (!normalizedSlug) {
    return false;
  }

  return specialties.some((specialty) => {
    const normalizedSpecialtyName = normalizeForComparison(specialty.name);
    return normalizedSpecialtyName === normalizedSlug;
  });
};

const findSpecialtyFromParam = (
  name: string,
  specialties: Specialties
): { value: string; label: string } => {
  const normalizedSearchName = normalizeForComparison(name);

  const specialty = specialties.find((x) => {
    return normalizeForComparison(x.name) === normalizedSearchName;
  });

  return {
    value: specialty?.id || '',
    label: specialty?.name || name,
  };
};

const normalizeGenders = (selectedGenders: string | null) =>
  selectedGenders
    ? selectedGenders.split(',').map((gender) => upperFirst(gender.toLocaleLowerCase()))
    : null;

export function getDefaultSearchValue(specialties: Specialties): ListingData {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const { pathname } = window.location;

  // Check for specialty in path with /specialty/ prefix
  const specialtyInPath = pathname.includes('/specialty/') ? pathname.split('/specialty/') : null;
  let specialtyFromPath = specialtyInPath ? specialtyInPath[1].split('/')[0] : undefined;

  // If no /specialty/ prefix found, check if the first path segment after /physicians is a specialty
  if (!specialtyFromPath) {
    const pathSegments = pathname.split('/').filter((segment) => segment);
    const physiciansIndex = pathSegments.indexOf(Pages.Physicians);

    if (physiciansIndex !== -1 && pathSegments.length > physiciansIndex + 1) {
      const potentialSpecialtySlug = pathSegments[physiciansIndex + 1];

      const mainRoutes = Object.values(Pages).filter((x) => x === Pages.Physicians) as string[];

      // Check if the potential specialty slug matches any specialty,
      // and is not one of the known main routes
      if (!mainRoutes.includes(potentialSpecialtySlug)) {
        const foundSpecialty = find(pathSegments, (segment) =>
          isSpecialtySlug(segment, specialties)
        );
        if (foundSpecialty) {
          specialtyFromPath = foundSpecialty;
        }
      }
    }
  }

  const defaultQueryParams = getQueryParams();

  const searchSpecialtyParam = defaultQueryParams?.specialtyName || defaultQueryParams?.specialty;

  let defaultSpecialty = { value: '', label: '' };

  // Set default specialty from path or query params
  if (specialtyFromPath) {
    defaultSpecialty = findSpecialtyFromParam(specialtyFromPath, specialties);
  } else if (searchSpecialtyParam) {
    defaultSpecialty = findSpecialtyFromParam(searchSpecialtyParam, specialties);
  } else if (defaultQueryParams?.specialtyId && !defaultQueryParams?.specialtyName) {
    // Todo: should we even support this case where only specialtyId is provided in the query params?
    const specialty = specialties.find((x) => x.id === defaultQueryParams?.specialtyId);

    defaultSpecialty = {
      value: defaultQueryParams?.specialtyId,
      label: specialty?.name || defaultQueryParams?.specialtyId,
    };
  }

  const defaultKeywordExact = defaultQueryParams?.keywordExact
    ? { value: defaultQueryParams?.keywordExact, label: defaultQueryParams?.keywordExact }
    : undefined;

  const defaultLocationUrlTitle =
    defaultQueryParams?.locationUrlTitle && defaultQueryParams?.locationUrlTitleLabel
      ? {
          value: defaultQueryParams?.locationUrlTitle,
          label: defaultQueryParams?.locationUrlTitleLabel,
        }
      : undefined;

  if (defaultSpecialty.label || defaultSpecialty.value) {
    return {
      type: AutocompleteQueries.specialtyId,
      value: defaultSpecialty.value,
      label: defaultSpecialty.label,
    };
  }

  if (defaultKeywordExact) {
    return {
      type: AutocompleteQueries.keywordExact,
      value: defaultKeywordExact.value,
      label: defaultKeywordExact.label,
    };
  }

  if (defaultLocationUrlTitle) {
    return {
      type: AutocompleteQueries.locationUrlTitle,
      value: defaultLocationUrlTitle.value,
      label: defaultLocationUrlTitle.label,
    };
  }

  if (
    !defaultSpecialty.label &&
    !defaultSpecialty.value &&
    !defaultKeywordExact &&
    !defaultLocationUrlTitle &&
    defaultQueryParams?.keywords
  ) {
    return {
      type: AutocompleteQueries.keywords,
      value: defaultQueryParams?.keywords,
      label: defaultQueryParams?.keywords,
    };
  }

  return undefined;
}

export const getQueryParams = ():
  | {
      zip: string;
      selectedGenders: string[] | null;
      selectedLanguages: string[] | null;
      showAcceptingNewPatientsOnly: boolean;
      teleMedicineFlg: boolean;
      facilityCoid: string | null;
      keywordExact: string | null;
      keywords: string | null;
      specialtyId: string | null;
      specialty: string | null;
      specialtyName: string | null; // functions the same as specialty
      locationUrlTitle: string | null;
      locationUrlTitleLabel: string | null;
    }
  | undefined => {
  if (typeof window !== 'undefined') {
    const queryParams = new URLSearchParams(window.location.search);

    const zip = queryParams.get(ListingQueryParams.zip) ?? '';
    const selectedGenders = queryParams.get(ListingQueryParams.gender);
    const selectedLanguages = queryParams.get(ListingQueryParams.languages);
    const showAcceptingNewPatientsOnly =
      queryParams.get(ListingQueryParams.acceptingNewPatientsOnly) === 'true';
    const teleMedicineFlg = queryParams.get(ListingQueryParams.teleMedicineFlg) === 'true';
    const facilityCoid =
      queryParams.get(ListingQueryParams.facilityCoid) &&
      queryParams.get(ListingQueryParams.locationUrlTitle)
        ? `${queryParams.get(ListingQueryParams.facilityCoid)}|${queryParams.get(ListingQueryParams.locationUrlTitle)}`
        : queryParams.get(ListingQueryParams.facilityCoid);
    const keywordExact = queryParams.get(ListingQueryParams.keywordExact);
    const keywords = queryParams.get(ListingQueryParams.keywords);
    const specialtyId = queryParams.get(ListingQueryParams.specialtyId);
    const specialtyName = queryParams.get(ListingQueryParams.specialtyName);
    const specialty = queryParams.get(ListingQueryParams.specialty);
    const locationUrlTitle = queryParams.get(ListingQueryParams.locationUrlTitle);
    const locationUrlTitleLabel = queryParams.get(ListingQueryParams.locationUrlTitleLabel);

    // Note: A bug was raised as genders param is sometimes coming as ?genders=male which causes our UI to not properly
    // handle the selected genders. We need to normalize the genders to have the first letter uppercase for consistency.
    const normalizedGenders = normalizeGenders(selectedGenders);

    return {
      zip,
      selectedGenders: normalizedGenders,
      selectedLanguages: selectedLanguages ? selectedLanguages?.split(';') : null,
      showAcceptingNewPatientsOnly,
      teleMedicineFlg,
      facilityCoid,
      keywordExact,
      keywords,
      specialtyId,
      specialtyName,
      specialty,
      locationUrlTitle,
      locationUrlTitleLabel,
    };
  }

  return undefined;
};

export function generateTodayDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Ensure two digits
  const day = String(now.getDate()).padStart(2, '0'); // Ensure two digits
  return `${year}${month}${day}`;
}

export const getPhysicianFormattedName = ({
  firstName,
  middleName,
  lastName,
  designation,
}: {
  firstName: string;
  middleName: string;
  lastName: string;
  designation: string;
}): string => {
  return `${firstName} ${middleName} ${lastName}, ${designation}`;
};
